import { computed, inject, Injectable, signal } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { AccountInfo } from '@azure/msal-browser';
import { parseAplications } from '@domain/aplication/parse-aplications';
import { environment } from '@environments/environment';
import { AplicationService } from '@services/aplication/aplication.service';
import { readRolesFromAccessToken } from './access-token';

export type UserInfo = {
    givenName: string;
    familyName: string;
    name: string;
    email: string;
    oid: string;
    upn: string;
};

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    private readonly msalService = inject(MsalService);
    private readonly aplicationService = inject(AplicationService);
    private readonly _user = signal<UserInfo | null>(null);

    readonly user = this._user.asReadonly();
    readonly isAuthenticated = computed(() => this._user() !== null);

    async loadUserFromAccount(): Promise<void> {
        const account = this.msalService.instance.getActiveAccount();

        if (!account) {
            this._user.set(null);
            this.aplicationService.setAplications([]);
            return;
        }

        const claims = account.idTokenClaims;

        this._user.set({
            givenName: (claims?.['given_name'] as string) ?? '',
            familyName: (claims?.['family_name'] as string) ?? '',
            name: (claims?.['name'] as string) ?? '',
            email: account.username ?? '',
            oid: (claims?.['oid'] as string) ?? '',
            upn: (claims?.['upn'] as string) ?? (claims?.['email'] as string) ?? account.username ?? '',
        });

        const roles = await this.resolveRoles(account, claims);
        this.aplicationService.setAplications(parseAplications(roles));
    }

    getUser(): UserInfo | null {
        return this._user();
    }

    logout(): void {
        this.msalService.logoutRedirect();
    }

    /**
     * Prefers `roles` from the ID token claims (no extra round-trip). Falls back to a
     * silent access-token acquisition + decode when the ID token has no roles, mirroring
     * the scope-request pattern in `auth.interceptor.ts`. Never throws: a failed silent
     * acquisition resolves to no roles.
     */
    private async resolveRoles(account: AccountInfo, claims: AccountInfo['idTokenClaims']): Promise<string[]> {
        const claimRoles = claims?.['roles'];

        if (Array.isArray(claimRoles) && claimRoles.length > 0) {
            return claimRoles.filter((role): role is string => typeof role === 'string');
        }

        try {
            const result = await this.msalService.instance.acquireTokenSilent({
                scopes: [`api://${environment.ENTRA_ID_CLIENT_ID}/Read`],
                account,
            });

            return readRolesFromAccessToken(result.accessToken);
        } catch {
            return [];
        }
    }
}
